import BackendExportModal from "custom_modules/ReportsNew/BackendExportModal";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import {
  ExportButtonIcon,
  ExportButtonWithIcon,
} from "styledComponents/buttons";

import ExportIcon from "../../../assets/img/icons/export_icon.svg";
import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";
import RightSlidingPanel from "../../../components/RightSlidingPanel/RightSlidingPanel";
import { setPathName } from "../../../filterReducer";
import {
  CommonFlex,
  CommonGrid,
  PageTitle,
} from "../../../styledComponents/common";
import { TimesheetContainer, StickyFilterContainer } from "../../../styledComponents/timesheet";
import TimesheetDetails from "../TimesheetDetailsPanel";
import TimesheetFilter from "../TimesheetFilter";
import WeeklyAdminView from "./WeeklyAdminView";
import WeeklyMemberView from "./WeeklyMemberView";
import { useScrollShadow } from "../../../customHooks/useScrollShadow";
const Weekly = ({
  selectedOrganization,
  profileData,
  clearTimesheetList,
  getWeeklyTimesheetDetails,
  getOtherTimesheets,
  getDailyTimesheets,
  isLoading,
  timesheetDetails,
  organizationMembersList,
  dailyTimesheetLoading,
  dailyTimesheets,
  timesheet,
  // Office
  getOfficeDetails,
  officeDetails,
  getOfficesShortList,
  officesShortList,
  officeDetailsLoading,
  // member short list
  memberShortListIsLoading,
  orgMembersShortList,
  getOrganizationMembersShortList,
  exportWeeklyTimeSheet,
  getAllMemberLastClockInOut,
  onlineMembers,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // local filters
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedTask, setSelectedTask] = useState("");
  const [selectedMember, setSelectedMember] = useState(""); // local only
  const [selectedOffice, setSelectedOffice] = useState({
    label: "(All Teams)",
    value: null,
  });

  const [startDate, setStartDate] = useState(
    moment().startOf("week").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("week").format("YYYY-MM-DD"),
  );

  const [detailsUserData, setDetailsUserData] = useState(null);
  const [detailsPanelIsOpen, setDetailsPanelIsOpen] = useState(false);

  const [officeMembers, setOfficeMembers] = useState([]);
  const [timeFormat, setTimeFormat] = useState("1h5m");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const [activeDate, setActiveDate] = useState(null);
  const [relevantDates, setRelevantDates] = useState([]);
  const [expandedAll, setExpandedAll] = useState(false);
  const [expandedTimesheets, setExpandedTimesheets] = useState([]);

  const [selectedPanelDate, setSelectedPanelDate] = useState(
    moment().startOf("week").format("YYYY-MM-DD"),
  );
  const showShadow = useScrollShadow(60);

  // collect dates for navigation
  useEffect(() => {
    if (timesheetDetails && Object.keys(timesheetDetails).length > 0) {
      const newDates = Object.values(timesheetDetails)
        .flatMap(items => items.map(item => item.date))
        .sort();
      setRelevantDates(newDates);
    }
  }, [timesheetDetails]);

  const disabledNext = useMemo(() => {
    if (moment(selectedPanelDate).isSame(moment(endDate), "day")) return true;
    const hasNextRelevantDate = relevantDates.some(date =>
      moment(date).isAfter(moment(selectedPanelDate)),
    );
    return !hasNextRelevantDate;
  }, [selectedPanelDate, relevantDates, endDate]);

  const disabledPrev = useMemo(() => {
    if (moment(selectedPanelDate).isSame(moment(startDate), "day")) return true;
    const hasPrevRelevantDate = relevantDates.some(date =>
      moment(date).isBefore(moment(selectedPanelDate)),
    );
    return !hasPrevRelevantDate;
  }, [selectedPanelDate, relevantDates, startDate]);

  const toggle = () => setIsExportModalOpen(p => !p);

  const handleExportTimesheetDetails = () => {
    const payload = {
      organization_id: selectedOrganization ? selectedOrganization.id : "",
      start_date: startDate,
      end_date: endDate,
      time_format: timeFormat,
    };
    if (selectedProject && selectedProject.value) {
      payload.project_id = selectedProject.value;
    }
    if (selectedTask && selectedTask.value) {
      payload.task_id = selectedTask.value;
    }
    if (selectedMember && selectedMember.id !== "") {
      payload.user_id = selectedMember.id;
    }
    if (
      ["owner", "admin", "team_leader"].includes(selectedOrganization?.role) &&
      !selectedMember
    ) {
      exportWeeklyTimeSheet(payload);
    }
    toggle();
  };

  const selectedOfficeMembers = v => setOfficeMembers(v);

  useEffect(() => {
    return () => {
      clearTimesheetList();
    };
  }, []);

  // when org changes, reset local filters only
  useEffect(() => {
    if (selectedOrganization && selectedOrganization.id) {
      getAllMemberLastClockInOut({ organization_id: selectedOrganization.id });

      // local reset only (not Redux)
      setSelectedMember("");
      setSelectedOffice({ label: "(All Teams)", value: null });

      const payload = {
        organization_id: selectedOrganization.id,
        start_date: startDate,
        end_date: endDate,
      };
      if (["owner", "admin"].includes(selectedOrganization.role)) {
        getWeeklyTimesheetDetails(payload);
      } else {
        getOtherTimesheets(payload);
      }
    }
  }, [selectedOrganization]);

  // select handlers that also update Redux
  const selectTeamMember = e => {
    if (!selectedOrganization?.id) return;
    const payload = {
      organization_id: selectedOrganization.id,
      start_date: startDate,
      end_date: endDate,
      user_id: e.id,
    };
    setSelectedMember(e);
    dispatch(
      setPathName({
        pathname: location.pathname,
        selectedMember: e,
        selectedOffice,
      }),
    );
    getOtherTimesheets(payload);
  };

  const clearMember = () => {
    if (!selectedOrganization?.id) return;
    const payload = {
      organization_id: selectedOrganization.id,
      start_date: startDate,
      end_date: endDate,
    };
    if (["owner", "admin"].includes(selectedOrganization.role)) {
      getWeeklyTimesheetDetails(payload);
    } else {
      getOtherTimesheets(payload);
    }
    setSelectedMember("");
    dispatch(
      setPathName({
        pathname: location.pathname,
        selectedMember: "",
      }),
    );
  };

  const selectDate = date => {
    const organizationId = selectedOrganization?.id;
    setStartDate(moment(date).startOf("week").format("YYYY-MM-DD"));
    setEndDate(moment(date).endOf("week").format("YYYY-MM-DD"));

    const payload = {
      organization_id: organizationId,
      start_date: moment(date).startOf("week").format("YYYY-MM-DD"),
      end_date: moment(date).endOf("week").format("YYYY-MM-DD"),
    };
    if (selectedMember && selectedMember.id !== "") {
      payload.user_id = selectedMember.id;
    }
    if (
      ["owner", "admin"].includes(selectedOrganization?.role) &&
      !selectedMember
    ) {
      getWeeklyTimesheetDetails(payload);
    } else {
      getOtherTimesheets(payload);
    }
  };

  const selectPanelDate = (date, userId) => {
    setSelectedPanelDate(moment(date).format("YYYY-MM-DD"));
    const payload = {
      organization_id: selectedOrganization.id,
      date: date.format("YYYY-MM-DD"),
      user_id: userId,
    };
    if (selectedMember && selectedMember.id !== "") {
      payload.user_id = selectedMember.id;
    }
    if (selectedOrganization.timezone) {
      payload.timezone = selectedOrganization.timezone;
    }
    getDailyTimesheets(payload);
  };

  const singlePanelDateChange = (direction, userId) => {
    setSelectedPanelDate(prevDate => {
      const currentDate = moment(prevDate).format("YYYY-MM-DD");
      let newDate = null;
      if (direction === "right") {
        newDate =
          relevantDates.find(date => date > currentDate) ||
          moment(prevDate).add(1, "days").format("YYYY-MM-DD");
      } else {
        newDate =
          [...relevantDates].reverse().find(date => date < currentDate) ||
          moment(prevDate).subtract(1, "days").format("YYYY-MM-DD");
      }
      selectPanelDate(moment(newDate), userId);
      return moment(newDate);
    });
  };

  const toggleDetailsPanel = value => {
    setDetailsPanelIsOpen(value);
    if (!value) setDetailsUserData(null);
  };

  const handleGetTimesheetDetails = (date, userId) => {
    if (selectedOrganization?.id && date && userId) {
      const payload = {
        organization_id: selectedOrganization.id,
        date: date.format("YYYY-MM-DD"),
        user_id: userId,
      };
      if (selectedOrganization.timezone) {
        payload.timezone = selectedOrganization.timezone;
      }
      getDailyTimesheets(payload);
      toggleDetailsPanel(true);
    }
  };

  const selectOffice = e => {
    const payload = {
      organization_id: selectedOrganization ? selectedOrganization.id : "",
    };
    if (e && e.value) {
      payload.office_id = e.value;
      getOfficeDetails(payload);
    }
    setSelectedMember("");
    setSelectedOffice(e);
    dispatch(
      setPathName({
        pathname: location.pathname,
        selectedOffice: e,
        selectedMember,
      }),
    );
  };

  const changeDateRange = e => {
    setStartDate(moment(e.startDate).format("YYYY-MM-DD"));
    setEndDate(moment(e.endDate).format("YYYY-MM-DD"));
  };

  const selectLastWeek = () => {
    setStartDate(
      moment().subtract(6, "days").startOf("week").format("YYYY-MM-DD"),
    );
    setEndDate(
      moment().subtract(6, "days").endOf("week").format("YYYY-MM-DD"),
    );
  };

  const selectCurrentWeek = () => {
    setStartDate(moment().startOf("week").format("YYYY-MM-DD"));
    setEndDate(moment().endOf("week").format("YYYY-MM-DD"));
  };

  const selectPrevWeek = () => {
    const prevStart = moment(startDate, "YYYY-MM-DD").subtract(1, "week");
    const prevEnd = moment(endDate, "YYYY-MM-DD").subtract(1, "week");

    setStartDate(prevStart.format("YYYY-MM-DD"));
    setEndDate(prevEnd.format("YYYY-MM-DD"));
  };

  const selectNextWeek = () => {
    const nextStart = moment(startDate, "YYYY-MM-DD").add(1, "week");
    const nextEnd = moment(endDate, "YYYY-MM-DD").add(1, "week");

    setStartDate(nextStart.format("YYYY-MM-DD"));
    setEndDate(nextEnd.format("YYYY-MM-DD"));
  };

  // re-fetch on date range change
  useEffect(() => {
    const payload = {
      organization_id: selectedOrganization ? selectedOrganization.id : "",
      start_date: startDate,
      end_date: endDate,
    };
    if (selectedMember && selectedMember.id !== "") {
      payload.user_id = selectedMember.id;
    }
    if (
      ["owner", "admin", "team_leader"].includes(selectedOrganization?.role) &&
      !selectedMember
    ) {
      getWeeklyTimesheetDetails(payload);
    } else {
      getOtherTimesheets(payload);
    }
  }, [startDate, endDate]);

  return (
    <div className="content">
      <RightSlidingPanel
        isOpen={detailsPanelIsOpen}
        closePanel={() => toggleDetailsPanel(false)}
        width="500px"
      >
        {dailyTimesheetLoading ? (
          <ComponentCircleLoader padding="35vh 0 " />
        ) : (
          <>
            <BackendExportModal
              isOpen={isExportModalOpen}
              toggle={toggle}
              excelExport={handleExportTimesheetDetails}
              title="Weekly Timesheet"
              timeFormat={timeFormat}
              setTimeFormat={setTimeFormat}
            />
            <TimesheetDetails
              timesheetsList={dailyTimesheets}
              userData={detailsUserData}
              selectedProject={selectedProject}
              selectedTask={selectedTask}
              fromPage={
                selectedOrganization &&
                ["admin", "owner"].includes(selectedOrganization.role) &&
                !selectedMember
                  ? "weeklyAdmin"
                  : "other"
              }
              timezone={selectedOrganization?.timezone}
              expandedAll={expandedAll}
              setExpandedAll={setExpandedAll}
              expandedTimesheets={expandedTimesheets}
              setExpandedTimesheets={setExpandedTimesheets}
              selectedPanelDate={moment(selectedPanelDate)}
              singlePanelDateChange={singlePanelDateChange}
              onPanelDateChange={selectPanelDate}
              disabledNext={disabledNext}
              disabledPrev={disabledPrev}
              setDetailsPanelIsOpen={setDetailsPanelIsOpen}
            />
          </>
        )}
      </RightSlidingPanel>

      <CommonGrid alignItem="center">
        <PageTitle>Weekly Timesheet</PageTitle>
      </CommonGrid>
      <StickyFilterContainer showShadow={showShadow}>
      <CommonFlex
        alignItems="flex-end"
        justifyContent="space-between"
        flexFlow="row wrap"
      >
        <TimesheetFilter
          showShadow={showShadow}
          page="weekly"
          selectedDate={moment(startDate)}
          startDate={startDate}
          endDate={endDate}
          onDateChange={selectDate}
          selectedMember={selectedMember}
          selectMember={selectTeamMember}
          clearMember={clearMember}
          officeDetails={officeDetails}
          getOfficesShortList={getOfficesShortList}
          officesShortList={officesShortList}
          selectedOffice={selectedOffice}
          selectOffice={selectOffice}
          getOrganizationMembersList={getOrganizationMembersShortList}
          organizationMembersList={orgMembersShortList}
          selectedOrganization={selectedOrganization}
          selectedOfficeMembers={selectedOfficeMembers}
          changeDateRange={changeDateRange}
          selectCurrentWeek={selectCurrentWeek}
          selectLastWeek={selectLastWeek}
          selectNextWeek={selectNextWeek}
          selectPrevWeek={selectPrevWeek}
          setSelectCurrentWeek={selectCurrentWeek}
        />
        {["admin", "owner", "team_leader"].includes(
          selectedOrganization?.role,
        ) &&
          !selectedMember && (
            <ExportButtonWithIcon showShadow={showShadow} onClick={() => toggle()}>
              <ExportButtonIcon src={ExportIcon} alt="" size="15px" />
              Export
            </ExportButtonWithIcon>
          )}
      </CommonFlex>
      </StickyFilterContainer>

      <TimesheetContainer>
        {isLoading || officeDetailsLoading ? (
          <ComponentCircleLoader />
        ) : selectedOrganization &&
          ["admin", "owner"].includes(selectedOrganization.role) &&
          !selectedMember ? (
          <WeeklyAdminView
            selectedDate={moment(startDate)}
            timesheetsList={timesheetDetails}
            membersList={
              selectedOffice && selectedOffice.label !== "(All Teams)"
                ? officeMembers
                : orgMembersShortList || ""
            }
            getTimesheetDetails={handleGetTimesheetDetails}
            detailsUserData={detailsUserData}
            setDetailsUserData={setDetailsUserData}
            selectedPanelDate={selectedPanelDate}
            setSelectedPanelDate={setSelectedPanelDate}
            activeDate={activeDate}
            setActiveDate={setActiveDate}
            isDetailsOpen={detailsPanelIsOpen}
            onlineMembers={onlineMembers}
          />
        ) : selectedOrganization ? (
          <WeeklyMemberView
            profileData={profileData}
            selectedDate={moment(startDate)}
            selectedMember={selectedMember}
            timesheetsList={timesheet}
            getTimesheetDetails={handleGetTimesheetDetails}
            detailsUserData={detailsUserData}
            setDetailsUserData={setDetailsUserData}
            activeDate={activeDate}
            setActiveDate={setActiveDate}
            selectedPanelDate={selectedPanelDate}
            setSelectedPanelDate={setSelectedPanelDate}
            isDetailsOpen={detailsPanelIsOpen}
          />
        ) : null}
      </TimesheetContainer>
    </div>
  );
};

export default Weekly;
